import React, { useMemo, useState } from 'react';
import EditorParent from '@/base-components/EditorParent';
import { Box, Button, Center, Flex, GridItem, Input, SimpleGrid, Spacer } from '@chakra-ui/react';
import { Context } from '@/App';
import { RoutePath, SPACING } from '@/generic/config';
import { useQuery, useQueryClient } from 'react-query';
import { customerName } from '@/generic/customerName';
import useToast from '@/base-components/hooks/useToast';
import useDialog from '@/base-components/hooks/useDialog';
import Dropdown from '@/base-components/Dropdown';
import Resource from '@/model/resource';
import Product from '@/model/product';
import TaskCalendar from './TaskCalendar';
import Text from '@/base-components/Text';
import usePalette from '@/base-components/hooks/usePalette';
import { Link } from 'react-router-dom';
import Icon, { Icons, productTaskIcon } from '@/base-components/Icons';
import { capitalizeFirstLetter } from '@/generic/string-utils';
import Divider from '@/base-components/Divider';
import { completeTask, getTasks, pauseTask, resumeTask, TaskItem } from '@/api/task-controller';

/** Google Maps URLs take at most 9 stops (8 waypoints + the destination) on phones. */
const MAX_ROUTE_STOPS = 9;
const MIN_PAUSE_REASON = 10;

/** One count shown on a task card: a product switched on for the Tasks page (or, as a fallback, a row on the order). */
type CountColumn = { productId: number, code: string, icon: string | null | undefined };

const TasksPage: React.FC = () => {
    const { translations, resources, products } = React.useContext(Context);
    const resourcesDropdownItems = resources?.data?.map(resource => ({ id: resource.id, name: resource.code }));
    const [currentResource, setCurrentResource] = useState<Resource | null>(null);
    // Today as UTC midnight of the LOCAL date — the calendar and API work in UTC days. Plain
    // new Date() made the page open on yesterday between 00:00 and 01:00/02:00 Swedish time.
    const [filterStartDate, setFilterStartDate] = useState<Date>(() => {
        const now = new Date();
        return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    });
    const [selectedId, setSelectedId] = useState<number | null>(null);

    const dayKey = filterStartDate.toISOString().split("T")[0];
    const tasksQuery = useQuery(['tasks', dayKey, currentResource?.id ?? null],
        () => getTasks(filterStartDate, currentResource?.id ?? null));
    const tasks = useMemo(() => tasksQuery.data ?? [], [tasksQuery.data]);

    // Products the tenant switched on for the Tasks page, in their chosen order.
    const shownProducts: Product[] = useMemo(() => (products?.data ?? [])
        .filter(p => p.showOnTasks && p.enabled)
        .sort((a, b) => (a.taskSort ?? 0) - (b.taskSort ?? 0) || a.code.localeCompare(b.code)), [products?.data]);

    const openRoute = () => {
        const stops = tasks.filter(t => !t.order.blocked).slice(0, MAX_ROUTE_STOPS);
        if (stops.length === 0) {
            return;
        }
        window.open(routeUrl(stops), "_blank", "noopener,noreferrer");
    };
    const routeStopCount = Math.min(MAX_ROUTE_STOPS, tasks.filter(t => !t.order.blocked).length);

    return (
        <EditorParent title={translations.tasks.title} description={translations.tasks.description}>
            <SimpleGrid columns={{ base: 4, md: 10 }} gap={SPACING} mt={SPACING} mb={SPACING}>
                <GridItem colSpan={{ base: 4, md: 2 }}>
                    {resourcesDropdownItems && <Dropdown title={translations.model.order.resource} items={resourcesDropdownItems} value={currentResource ? { id: currentResource.id, name: currentResource.code } : null} noSelectionTitle={translations.model.order.allResources} onChange={(item) => setCurrentResource(item ? resources?.data?.find(resource => resource.id === item.id) || null : null)} />}
                </GridItem>
                <GridItem colSpan={{ base: 4, md: 2 }}>
                    <TaskCalendar
                        label={translations.model.order.filterDate}
                        selectedDate={filterStartDate}
                        onSelectDate={setFilterStartDate}
                        resourceId={currentResource?.id ?? null}
                    />
                </GridItem>
            </SimpleGrid>
            <Button w="100%" size="lg" mb={SPACING} disabled={routeStopCount === 0} onClick={openRoute} data-testid="next-route">
                <Icon icon={Icons.route} />
                {routeStopCount === 0 ? translations.tasks.noRoute : translations.tasks.nextRoute(routeStopCount)}
            </Button>
            <DayHeader date={filterStartDate} />
            <Flex direction="column" gap={SPACING} mt={SPACING} pt={SPACING}>
                {tasks.map(task => (
                    <TaskCard
                        key={task.order.id}
                        task={task}
                        columns={columnsFor(task, shownProducts)}
                        selected={selectedId === task.order.id}
                        onToggle={() => setSelectedId(id => id === task.order.id ? null : task.order.id)}
                    />
                ))}
            </Flex>
            {!tasksQuery.isLoading && tasks.length === 0 && <Center><Text mt={SPACING}>{translations.tasks.noTasksFound}</Text></Center>}
        </EditorParent>
    );
};

export default TasksPage;

const columnsFor = (task: TaskItem, shownProducts: Product[]): CountColumn[] => {
    if (shownProducts.length > 0) {
        return shownProducts.map(p => ({ productId: p.id, code: p.code, icon: p.taskIcon }));
    }
    // Nothing switched on yet: show what is on the order.
    return task.rows.map(r => ({ productId: r.productId, code: r.productCode, icon: r.productIcon }));
};

const addressLine = (task: TaskItem) => {
    const w = task.order.worksiteHit;
    return [w.address, w.city, w.postalCode].filter(Boolean).join(", ");
};

/** "Gatan 1 & 3" confuses Maps; like the old crew app, route to the first part. */
const mapsQuery = (task: TaskItem) => {
    const w = task.order.worksiteHit;
    return [(w.address ?? "").split("&")[0].trim(), w.postalCode, w.city, "Sweden"].filter(Boolean).join(", ");
};

const navigateUrl = (task: TaskItem) =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsQuery(task))}&travelmode=driving`;

/** From the current position through the stops in list order; the last stop is the destination. */
const routeUrl = (stops: TaskItem[]) => {
    const destination = encodeURIComponent(mapsQuery(stops[stops.length - 1]));
    const waypoints = stops.slice(0, -1).map(t => encodeURIComponent(mapsQuery(t))).join("%7C");
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}`
        + (waypoints ? `&waypoints=${waypoints}` : "")
        + "&travelmode=driving";
};

const DayHeader = ({ date }: { date: Date }) => {
    const { language } = React.useContext(Context);
    const locale = language === 'SE' ? 'sv-SE' : 'en-US';

    return (
        <Box textAlign="center">
            <Divider />
            <Text variant="title">
                {capitalizeFirstLetter(date.toLocaleDateString(locale, { weekday: 'long', timeZone: 'UTC' }))}
            </Text>
            <Divider />
        </Box>
    );
};

const TaskCard = ({ task, columns, selected, onToggle }: { task: TaskItem, columns: CountColumn[], selected: boolean, onToggle: () => void }) => {
    const { translations } = React.useContext(Context);
    const { colorScheme, background, accent } = usePalette();
    const order = task.order;
    const worksite = order.worksiteHit;
    const customer = worksite.customerHit;
    const orderedOf = (productId: number) => task.rows.find(r => r.productId === productId)?.orderedQuantity ?? 0;

    return (
        <Box
            textAlign="start"
            shadow="sm"
            borderRadius="md"
            borderWidth={order.blocked ? "3px" : "1px"}
            borderColor={order.blocked ? colorScheme.danger : "border"}
            outline={selected ? `2px solid ${accent}` : undefined}
            p={SPACING * 2}
            pt={SPACING}
            data-testid="task-card"
        >
            <Flex gap={SPACING}>
                {order.express && (
                    <Text my={SPACING} flex={1} variant="small" fontWeight="bold" bg={colorScheme.warning} textAlign="center" borderRadius={SPACING}>{translations.model.order.express.title}</Text>
                )}
                {order.blocked && (
                    <Text my={SPACING} flex={1} variant="small" fontWeight="bold" bg={colorScheme.danger} textAlign="center" borderRadius={SPACING}>{translations.tasks.onHold}</Text>
                )}
            </Flex>

            {/* Header: tap to open/close */}
            <Flex gap={SPACING} alignItems="center" cursor="pointer" onClick={onToggle} data-testid="task-toggle">
                <Icon icon={Icons.map} />
                <Text variant="subtitle" fontWeight="bold">{addressLine(task)}</Text>
                {worksite.externalId && <Text variant="subtitle">({worksite.externalId})</Text>}
                <Spacer />
                {order.resource?.code && <Text fontWeight="semibold">{order.resource.code}</Text>}
                <Icon icon={selected ? Icons.chevronUp : Icons.chevronDown} />
            </Flex>

            {/* Counts */}
            {columns.length > 0 && (
                <Flex gap={SPACING * 4} mt={SPACING} wrap="wrap" data-testid="task-counts">
                    {columns.map(c => (
                        <Flex key={c.productId} gap={SPACING} alignItems="center" title={c.code}>
                            <Icon icon={productTaskIcon(c.icon)} />
                            <Text fontWeight="semibold">{orderedOf(c.productId)}</Text>
                            <Text variant="small" color="fg.muted">{c.code}</Text>
                        </Flex>
                    ))}
                    {(task.recurringInterval ?? 0) > 0 && (
                        <Flex gap={SPACING} alignItems="center" color={colorScheme.success} title={translations.tasks.recurring}>
                            <Icon icon={Icons.recurring} />
                        </Flex>
                    )}
                </Flex>
            )}

            {worksite.contactPhone && <PhoneLine label={translations.tasks.worksitePhone} phone={worksite.contactPhone} />}
            <Flex gap={SPACING} alignItems="center" mt={SPACING}>
                <Icon icon={Icons.Customer} />
                <Text fontWeight="semibold">{customerName(customer)}</Text>
                <Text>{customer?.externalId ? `(${customer.externalId})` : ''}</Text>
            </Flex>
            {customer?.phone && <PhoneLine label={translations.tasks.customerPhone} phone={customer.phone} />}

            {/* What the crew must know before going in */}
            {task.dispatcherNotes && task.dispatcherNotes.trim() !== "" && (
                <Flex gap={SPACING} alignItems="start" mt={SPACING} p={SPACING} borderRadius="md" bg={background.darker} data-testid="task-dispatcher-notes">
                    <Box pt="2px"><Icon icon={Icons.comment} /></Box>
                    <Text whiteSpace="pre-wrap">{task.dispatcherNotes}</Text>
                </Flex>
            )}
            {task.fields.map(f => (
                <Flex key={f.key} gap={SPACING} alignItems="center" mt={SPACING}>
                    <Icon icon={Icons.field} />
                    <Text>{f.key}: <b>{f.value === "true" ? translations.common.yes : f.value}</b></Text>
                </Flex>
            ))}
            {order.blocked && task.blockedReason && (
                <Flex gap={SPACING} alignItems="start" mt={SPACING} color={colorScheme.danger}>
                    <Box pt="2px"><Icon icon={Icons.pause} /></Box>
                    <Text whiteSpace="pre-wrap">{translations.tasks.holdReason}: {task.blockedReason}</Text>
                </Flex>
            )}

            {selected && <TaskActions task={task} columns={columns} />}
        </Box>
    );
};

const PhoneLine = ({ label, phone }: { label: string, phone: string }) => (
    <Flex gap={SPACING} alignItems="center" w="fit-content" cursor="pointer" mt={SPACING / 2} onClick={() => window.location.href = `tel:${phone}`}>
        <Text>{label}: {phone}</Text>
        <Icon icon={Icons.phone} size='xs' />
    </Flex>
);

/** The opened card: counts to change, a comment, then Navigate / Pause / Complete — like the old crew app. */
const TaskActions = ({ task, columns }: { task: TaskItem, columns: CountColumn[] }) => {
    const { translations } = React.useContext(Context);
    const toast = useToast();
    const { openDialog } = useDialog();
    const queryClient = useQueryClient();
    const order = task.order;
    const orderedOf = (productId: number) => task.rows.find(r => r.productId === productId)?.orderedQuantity ?? 0;

    const [counts, setCounts] = useState<Record<number, string>>(
        () => Object.fromEntries(columns.map(c => [c.productId, String(orderedOf(c.productId))])));
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const valueOf = (productId: number) => {
        const n = Number.parseInt(counts[productId] ?? "", 10);
        return Number.isNaN(n) ? 0 : n;
    };
    const hasNegative = columns.some(c => valueOf(c.productId) < 0);
    const step = (productId: number, delta: number) =>
        setCounts(prev => ({ ...prev, [productId]: String(Math.max(0, valueOf(productId) + delta)) }));

    const refresh = () => {
        queryClient.invalidateQueries('tasks');
        queryClient.invalidateQueries('dailyCounts');
    };

    const run = async (action: () => Promise<void>, doneText: string) => {
        setBusy(true);
        try {
            await action();
            toast(doneText, "", "success");
            refresh();
        } catch {
            // The API error toast already explains what went wrong.
        } finally {
            setBusy(false);
        }
    };

    const onPause = () => {
        if (order.blocked) {
            run(() => resumeTask(order.id), translations.tasks.resumed);
            return;
        }
        if (comment.trim().length < MIN_PAUSE_REASON) {
            toast(translations.tasks.pauseTooShort(MIN_PAUSE_REASON), "", "warning");
            return;
        }
        // Clear the comment once it became the pause reason, so it is not reused as the completion comment.
        run(async () => { await pauseTask(order.id, comment.trim()); setComment(""); }, translations.tasks.paused);
    };

    const onComplete = () => {
        if (hasNegative) {
            toast(translations.tasks.negative, "", "warning");
            return;
        }
        const lines = columns.map(c => {
            const diff = valueOf(c.productId) - orderedOf(c.productId);
            const note = diff === 0 ? "" : ` (${Math.abs(diff)} ${diff < 0 ? translations.tasks.missing : translations.tasks.extra})`;
            return { text: `${c.code}: ${valueOf(c.productId)}${note}`, warn: diff !== 0 };
        });
        openDialog({
            title: translations.tasks.confirmTitle,
            body: (
                <Box data-testid="complete-confirm">
                    {lines.map((l, i) => <Text key={i} fontWeight={l.warn ? "bold" : "normal"}>{l.text}</Text>)}
                    {comment.trim() && <Text mt={SPACING} whiteSpace="pre-wrap">{translations.tasks.comment}: {comment.trim()}</Text>}
                    <Text mt={SPACING} color="fg.muted">{translations.tasks.address}: {addressLine(task)}</Text>
                </Box>
            ),
            buttons: [
                { title: translations.common.Cancel, onClick: () => { } },
                {
                    title: translations.tasks.confirmButton,
                    onClick: () => run(() => completeTask(order.id,
                        columns.map(c => ({ productId: c.productId, actualQuantity: valueOf(c.productId) })),
                        comment), translations.tasks.completed),
                },
            ],
        });
    };

    return (
        <Box mt={SPACING * 2} data-testid="task-actions">
            <Divider />
            <SimpleGrid columns={{ base: 1, sm: Math.min(3, Math.max(1, columns.length)) }} gap={SPACING} mt={SPACING * 2}>
                {columns.map(c => (
                    <Box key={c.productId}>
                        <Flex gap={SPACING} alignItems="center" mb={SPACING / 2}>
                            <Icon icon={productTaskIcon(c.icon)} />
                            <Text weight="semibold">{c.code}</Text>
                        </Flex>
                        <Flex gap={SPACING}>
                            <Button variant="outline" onClick={() => step(c.productId, -1)} aria-label={`- ${c.code}`}><Icon icon={Icons.minus} /></Button>
                            <Input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                textAlign="center"
                                value={counts[c.productId] ?? ""}
                                onChange={(e) => setCounts(prev => ({ ...prev, [c.productId]: e.target.value }))}
                                aria-label={c.code}
                                data-testid={`count-${c.productId}`}
                            />
                            <Button variant="outline" onClick={() => step(c.productId, 1)} aria-label={`+ ${c.code}`}><Icon icon={Icons.plus} /></Button>
                        </Flex>
                    </Box>
                ))}
            </SimpleGrid>
            <Input mt={SPACING * 2} placeholder={translations.tasks.commentPlaceholder} value={comment} onChange={(e) => setComment(e.target.value)} aria-label={translations.tasks.comment} data-testid="task-comment" />
            <Flex direction="column" gap={SPACING} mt={SPACING * 2}>
                <Button size="lg" variant="outline" onClick={() => window.open(navigateUrl(task), "_blank", "noopener,noreferrer")}>
                    <Icon icon={Icons.map} /> {translations.tasks.navigate}
                </Button>
                <Button size="lg" variant="outline" onClick={onPause} disabled={busy} data-testid="task-pause">
                    <Icon icon={order.blocked ? Icons.play : Icons.pause} /> {order.blocked ? translations.tasks.resume : translations.tasks.pause}
                </Button>
                <Button size="lg" onClick={onComplete} disabled={busy || hasNegative} data-testid="task-complete">
                    <Icon icon={Icons.check} /> {translations.tasks.complete}
                </Button>
                <Flex justifyContent="end">
                    <Link to={`${RoutePath.Orders}/${order.id}?fromTasks=true`} target='_blank'>
                        <Flex gap={SPACING} alignItems="center"><Text variant="small">{translations.tasks.openOrder}</Text><Icon size="sm" icon={Icons.openLink} /></Flex>
                    </Link>
                </Flex>
            </Flex>
        </Box>
    );
};
